/**
 * Utility functions for input field validation
 */

import {
  INVALID_SPECIAL_CHARS_REGEX,
  SPECIAL_CHARS_VALIDATION_MESSAGE,
} from "../constants/validationConstants";

// Type definition for the toast function used throughout the app
export type ToastFunction = (
  message: any,
  type: "error" | "warning" | "success" | "warn",
  transition?: any
) => void;

/**
 * Validates if a string contains only alphanumeric characters and underscores
 * @param value - String to validate
 * @returns boolean indicating if valid
 */
export const isAlphanumericWithUnderscore = (value: string): boolean => {
  const regex = /^[a-zA-Z0-9_]*$/;
  return regex.test(value);
};

/**
 * Validates if a string contains spaces
 * @param value - String to validate
 * @returns boolean indicating if contains spaces
 */
export const containsSpaces = (value: string): boolean => {
  return value.includes(" ");
};

/**
 * Validates if a string contains special characters (excluding underscore)
 * @param value - String to validate
 * @returns boolean indicating if contains special characters
 */
export const containsSpecialCharacters = (value: string): boolean => {
  const specialCharRegex = /[^a-zA-Z0-9_]/;
  return specialCharRegex.test(value);
};

/**
 * Validates if a string contains any of the invalid special characters defined
 * in INVALID_SPECIAL_CHARS_REGEX (DQ-8614).
 * Password and Email fields are exempt from this check.
 * @param value - String to validate
 * @returns boolean — true if the value contains an invalid special character
 */
export const containsInvalidSpecialChars = (value: string): boolean => {
  return INVALID_SPECIAL_CHARS_REGEX.test(value);
};

/**
 * Yup-compatible test function for the no-special-characters rule (DQ-8614).
 * Use with .test("no-special-chars", SPECIAL_CHARS_VALIDATION_MESSAGE, noSpecialCharsYupTest)
 * on any Yup string field that must reject invalid special characters.
 * @param value - Field value passed by Yup (may be undefined for optional fields)
 * @returns true if the value is absent or contains no invalid special characters
 */
export const noSpecialCharsYupTest = (value: string | undefined): boolean => {
  if (!value) return true; // let .required() handle empty values
  return !containsInvalidSpecialChars(value);
};

export { SPECIAL_CHARS_VALIDATION_MESSAGE };

/**
 * Validates input for dependency marker fields
 * @param value - Input value to validate
 * @param showToast - Toast function to show error messages
 * @returns boolean indicating if validation passed
 */
export const validateDependencyMarkerInput = (
  value: string,
  showToast: ToastFunction
): boolean => {
  if (containsSpaces(value)) {
    showToast("Spaces are not allowed in this field", "error");
    return false;
  }

  if (containsSpecialCharacters(value)) {
    showToast(
      "Special characters are not allowed. Only letters, numbers, and underscore are allowed.",
      "error"
    );
    return false;
  }

  return true;
};

/**
 * Validates input for external source name fields
 * @param value - Input value to validate
 * @param showToast - Toast function to show error messages
 * @returns boolean indicating if validation passed
 */
export const validateExternalSourceNameInput = (
  value: string,
  showToast: ToastFunction
): boolean => {
  if (containsSpaces(value)) {
    showToast("Spaces are not allowed in External Source Name", "error");
    return false;
  }

  if (containsSpecialCharacters(value)) {
    showToast(
      "Special characters are not allowed in External Source Name. Only letters, numbers, and underscore are allowed.",
      "error"
    );
    return false;
  }

  return true;
};

/**
 * Handles keydown events for alphanumeric + underscore only fields
 * @param e - Keyboard event
 * @param showToast - Toast function to show error messages
 * @param fieldName - Name of the field for error messages
 */
export const handleAlphanumericKeyDown = (
  e: React.KeyboardEvent,
  showToast: ToastFunction,
  fieldName: string = "field"
): void => {
  const char = e.key;

  // Prevent space
  if (char === " ") {
    e.preventDefault();
    showToast(`Spaces are not allowed in ${fieldName}`, "error");
    return;
  }

  // Allow navigation and control keys
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "Escape",
    "Enter",
  ];

  // Only allow alphanumeric, underscore, and navigation keys
  if (!/[a-zA-Z0-9_]/.test(char) && !allowedKeys.includes(char)) {
    e.preventDefault();
    showToast(
      `Special characters are not allowed in ${fieldName}. Only letters, numbers, and underscore are allowed.`,
      "error"
    );
  }
};

/**
 * Handles change events for alphanumeric + underscore only fields
 * @param value - Input value
 * @param showToast - Toast function to show error messages
 * @param fieldName - Name of the field for error messages
 * @returns boolean indicating if change should be allowed
 */
export const handleAlphanumericChange = (
  value: string,
  showToast: ToastFunction,
  fieldName: string = "field"
): boolean => {
  if (containsSpaces(value)) {
    showToast(`Spaces are not allowed in ${fieldName}`, "error");
    return false;
  }

  if (containsSpecialCharacters(value)) {
    showToast(
      `Special characters are not allowed in ${fieldName}. Only letters, numbers, and underscore are allowed.`,
      "error"
    );
    return false;
  }

  return true;
};
